import {
	ButtonUtility,
	DatePicker,
	FilterField,
	LoadingIndicator,
	PageHeader,
} from "@components";
import { Button } from "@components/button/button-untitledui";
import { Copy01, Share04, Zap } from "@untitledui/icons";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { DASHBOARD_SECTION_CARD_CLASS } from "../constants/dashboard-section-card.constants";
import { useDashboardEventsMetricsDateState } from "../hooks/use-dashboard-events-metrics-date-state";
import { useEventsMetrics } from "../hooks/use-events-metrics";
import { buildEventsMetricsChartRows } from "../utils/events-metrics-chart.utils";
import { buildEventsMetricsClipboardSummary } from "../utils/events-metrics-clipboard.utils";
import { DashboardEventsBarChart } from "./dashboard-events-bar-chart";

export function DashboardEventsMetricsSection() {
	const { startDate, endDate, setStartDate, setEndDate, metricsParams } =
		useDashboardEventsMetricsDateState();

	const { data, isLoading, isError, refetch } = useEventsMetrics(metricsParams);

	const chartRows = useMemo(() => buildEventsMetricsChartRows(data), [data]);

	const handleCopySummary = useCallback(async () => {
		if (!data) {
			return;
		}
		try {
			await navigator.clipboard.writeText(
				buildEventsMetricsClipboardSummary(data)
			);
			toast.success("Resumo copiado para a área de transferência.");
		} catch {
			toast.error("Não foi possível copiar.");
		}
	}, [data]);

	const renderContent = () => {
		if (isLoading) {
			return (
				<output className="flex min-h-[240px] flex-col items-center justify-center">
					<LoadingIndicator size="md" type="line-simple" />
					<span className="sr-only">Carregando eventos</span>
				</output>
			);
		}

		if (isError) {
			return (
				<div
					className="flex min-h-[240px] flex-col items-center justify-center gap-3 text-center"
					role="alert"
				>
					<p className="text-sm text-tertiary">
						Não foi possível carregar os eventos.
					</p>
					<Button
						color="secondary"
						onClick={() => {
							refetch();
						}}
						size="sm"
					>
						Tentar novamente
					</Button>
				</div>
			);
		}

		if (!data) {
			return null;
		}

		return (
			<div className="flex flex-col gap-6">
				<div className="border-secondary border-b">
					<p className="mb-1 font-medium text-sm text-tertiary leading-5">
						Total de Eventos
					</p>
					<p className="font-bold text-2xl text-primary leading-8 tracking-tight">
						{data.total}
					</p>
				</div>

				{data.total === 0 ? (
					<p className="text-center text-sm text-tertiary sm:text-left">
						Nenhum evento registrado neste período.
					</p>
				) : null}

				{data.total > 0 && chartRows.length === 0 ? (
					<p className="text-center text-sm text-tertiary sm:text-left">
						Sem detalhamento por tipo para este período.
					</p>
				) : null}

				{data.total > 0 && chartRows.length > 0 ? (
					<DashboardEventsBarChart rows={chartRows} />
				) : null}
			</div>
		);
	};

	return (
		<section
			aria-labelledby="dashboard-events-metrics-heading"
			className="mb-6"
		>
			<div
				className={`${DASHBOARD_SECTION_CARD_CLASS} flex flex-col gap-6 p-5 md:p-6`}
			>
				<PageHeader
					actions={
						<div className="flex items-center gap-2">
							<ButtonUtility
								color="secondary"
								icon={Copy01}
								isDisabled={!data}
								onClick={handleCopySummary}
								size="sm"
								tooltip="Copiar resumo das métricas"
							/>
							<ButtonUtility
								color="secondary"
								href="/tracking/events"
								icon={Share04}
								size="sm"
								tooltip="Abrir lista de eventos de telemetria"
							/>
						</div>
					}
					title={
						<div className="flex gap-3">
							<div
								aria-hidden
								className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E0F2FE]"
							>
								<Zap className="size-5 text-[#2E90FA]" />
							</div>
							<div className="flex flex-col">
								<span id="dashboard-events-metrics-heading">Eventos</span>
								<span className="text-secondary text-sm leading-5">
									Eventos de condução
								</span>
							</div>
						</div>
					}
					titleAs="h2"
				/>

				<div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
					<FilterField label="Data inicial">
						<DatePicker
							aria-label="Data inicial"
							className="w-full"
							maxValue={endDate}
							onChange={(date) => {
								if (!date) {
									return;
								}
								setStartDate(date);
							}}
							size="sm"
							value={startDate}
						/>
					</FilterField>
					<FilterField label="Data final">
						<DatePicker
							aria-label="Data final"
							className="w-full"
							minValue={startDate}
							onChange={(date) => {
								if (!date) {
									return;
								}
								setEndDate(date);
							}}
							size="sm"
							value={endDate}
						/>
					</FilterField>
				</div>

				<div className="relative flex min-h-[240px] flex-col">
					{renderContent()}
				</div>
			</div>
		</section>
	);
}
